import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import CardFace from '../Card/CardFace';
import { PlainButton } from '../Controls';
import useCardDrag from '../Drag/useCardDrag';
import { TRAINER } from './handCardAction';
import { AT_ONCE, ON_A_POKEMON } from '../Trainer/trainerPlay';
import { picksCancel, startPicks, submitPicks } from '../Picks/actions';
import { closeContextMenu } from '../ActiveGame/actions';
import { forgetDrop } from '../Drag/dropTargets';
import { Shake } from './styled';

/** Something only moved within the hand: a card that can't be played now. */
export const REARRANGE_ONLY = 'rearrange-only';
/** How long a card that can't be played says why, after you try to play it. */
const REFUSED_MS = 2200;

/**
 * A card in your hand you can tap (for its menu) or drag onto the board (useCardDrag). Dragging starts the card's
 * flow, which lights up where it can go, the same as tapping its menu button; letting go on a lit spot does what
 * tapping that spot would. A Trainer is dragged onto the Pokémon it goes on, or anywhere on the board when it goes
 * on none (trainerPlay). Any card can be moved to another place in your hand; one that can't be played now only
 * that, and let go anywhere else it shakes and says why.
 */
const DraggableHandCard = ({ card, place, action, onTap, onRefused }) => {
  const dispatch = useDispatch();
  const pendingUndo = useSelector((state) => state.undo);
  // Its move waits for an Undo: the card's back in the hand meanwhile, so it flies to where it goes once sent.
  useEffect(() => {
    if (pendingUndo) {
      forgetDrop();
    }
  }, [pendingUndo]);
  const handPosition = card.get('order');
  const trainer = action.kind === TRAINER ? action.play : null;

  // A Trainer starts the flow its menu button would: picking a Pokémon on the board, or nothing yet and played (or
  // its menu opened) wherever it's dropped on the board (dropAnywhere).
  const liftTrainer = () => {
    const { how, source, steps } = trainer;
    if (how === ON_A_POKEMON) {
      onTap();
      dispatch(startPicks(source, steps));
      return { kind: TRAINER, handPosition };
    }
    const dropAnywhere = how === AT_ONCE
      ? () => { dispatch(submitPicks(source)); }
      : () => {
        onTap();
        dispatch(startPicks(source, steps));
        return false;
      };
    return { kind: TRAINER, handPosition, dropAnywhere };
  };

  const backOut = () => {
    if (trainer && trainer.how === ON_A_POKEMON) {
      dispatch(picksCancel());
      dispatch(closeContextMenu());
    } else if (action.cancel) {
      dispatch(action.cancel());
    }
  };

  const [refused, setRefused] = useState(null);
  useEffect(() => {
    onRefused(refused);
    if (!refused) {
      return undefined;
    }
    const timer = setTimeout(() => setRefused(null), REFUSED_MS);
    return () => clearTimeout(timer);
  }, [refused, onRefused]);

  const { bind, ghost } = useCardDrag({
    enabled: true,
    place,
    // What's dragged says how to back out of its flow (backOut), for a drop back in the hand that moves the card.
    lift: () => {
      if (action.reason) {
        return { kind: REARRANGE_ONLY, handPosition, backOut: () => {} };
      }
      if (trainer) {
        return { ...liftTrainer(), backOut };
      }
      if (action.start) {
        dispatch(action.start());
      }
      return { kind: action.kind, handPosition, backOut };
    },
    onMiss: () => (action.reason ? setRefused(action.reason) : backOut()),
    // Moved within the hand, it's the same card at a new place: that's the game's answer. A move that waits for its
    // Undo (Switch) isn't sent yet: the card goes back to the hand while it waits.
    settledBy: `${handPosition}:${pendingUndo ? 'waiting' : ''}`,
    // Quick, nearly flat swipes scroll the hand.
    touchAction: 'pan-x',
  });

  return (
    <>
      <PlainButton {...bind} onClick={onTap} aria-label={card.get('name')}>
        <Shake $on={!!refused}><CardFace card={card} /></Shake>
      </PlainButton>
      {ghost(<CardFace card={card} />)}
    </>
  );
};

DraggableHandCard.propTypes = {
  card: PropTypes.instanceOf(Map).isRequired,
  /** The card's place on screen (Motion/anchorNames), so a card you drop doesn't fly there again. */
  place: PropTypes.string.isRequired,
  action: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    reason: PropTypes.string,
    start: PropTypes.func,
    cancel: PropTypes.func,
    play: PropTypes.object,
  }).isRequired,
  onTap: PropTypes.func.isRequired,
  /** Told why the card can't be played when you try to drag it, and null once that's said. */
  onRefused: PropTypes.func.isRequired,
};

export default DraggableHandCard;
